package com.nonopn.skywatcher.common.app.home

import com.dokar.sonner.TextToastAction
import com.dokar.sonner.ToasterState
import com.mmk.kmpauth.google.GoogleAuthCredentials
import com.mmk.kmpauth.google.GoogleAuthProvider
import com.nonopn.skywatcher.app.utils.AppBackPressProvider
import com.nonopn.skywatcher.app.utils.AuthentInit
import com.nonopn.skywatcher.app.utils.FirebaseWrapper
import com.nonopn.skywatcher.app.utils.safeAsync
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.ILoginCaller
import com.nonopn.skywatcher.common.SessionManager
import com.nonopn.skywatcher.common.app.api.impl.ApiAdmin
import com.nonopn.skywatcher.common.app.api.impl.ApiInterface
import com.nonopn.skywatcher.common.app.api.impl.ApiUser
import com.nonopn.skywatcher.common.app.navigation.PossibleRoutes
import com.nonopn.skywatcher.common.app.navigation.RouteParameterSentinel
import com.nonopn.skywatcher.common.app.navigation.RouterDetection
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetectionWithMetadata
import com.nonopn.skywatcher.common.app.portfolio.main.NoThunderDetectionSelectedModel
import com.nonopn.skywatcher.common.app.portfolio.selected.ThunderDetectionSelectedModel
import com.nonopn.skywatcher.common.app.portfolio.selected.ThunderDetectionSelectedModelCustomer
import com.nonopn.skywatcher.common.app.portfolio.selected.ThunderDetectionSelectedModelManager
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.alert_detected_on_sentinel
import com.nonopn.skywatcher.common.app.res.app_title_client
import com.nonopn.skywatcher.common.app.res.app_title_manager
import com.nonopn.skywatcher.common.app.res.go
import com.nonopn.skywatcher.common.app.res.warn_customer_text_client
import com.nonopn.skywatcher.common.app.res.warn_customer_text_manager
import com.nonopn.skywatcher.config.SharedConfig
import com.nonopn.skywatcher.design.init.InitStateProvider
import com.nonopn.skywatcher.maps.providers.GoogleProvider
import com.nonopn.skywatcher.maps.providers.VaisalaProvider
import eu.codlab.navigation.Navigation
import eu.codlab.permissions.PermissionsController
import eu.codlab.permissions.notifications.PermissionNotifications
import eu.codlab.sentry.wrapper.Sentry
import eu.codlab.viewmodel.StateViewModel
import eu.codlab.viewmodel.launch
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.getString
import kotlin.time.Duration.Companion.seconds

private const val Millis = 1000.0

@Suppress("TooManyFunctions")
abstract class AppModel(
    val isSystemDarkMode: Boolean
) : StateViewModel<AppModelState>(
    AppModelState(
        isSystemDarkMode = isSystemDarkMode
    )
), InitStateProvider, ILoginCaller {
    val googleApi = GoogleProvider()
    val vaisalaApi = VaisalaProvider()

    private val sessionManager = SessionManager(
        folder = "SkySentinel",
        SpecificData.serializer(),
        admin = { ApiAdmin(it) },
        normal = { ApiUser(it) },
    )
    var onBackPressed: AppBackPressProvider = AppBackPressProvider()

    abstract val warnCustomerText: StringResource

    abstract val appTitle: StringResource

    private val permissionsController = PermissionsController()

    var toaster: ToasterState? = null

    companion object {
        fun fake(): AppModel {
            return object : AppModel(false) {
                override val warnCustomerText: StringResource
                    get() = Res.string.warn_customer_text_client

                override val appTitle: StringResource
                    get() = Res.string.app_title_manager

                override fun warnCustomerAction() {
                    // nothing
                }

                override fun login(
                    email: String,
                    password: String,
                    callback: (err: Throwable?) -> Unit
                ) = Unit

                override fun createThunderDetectionSelectedModel(selectedThunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSelectedModel {
                    TODO("not implemented")
                }
            }
        }
    }

    override fun isInitialized() = states.value.initialized

    abstract fun warnCustomerAction()

    @Suppress("TooGenericExceptionCaught", "SwallowedException")
    override fun initialize() {
        safeLaunch(
            onError = {
                it.printStackTrace()
            }
        ) {
            googleApi.initialize()
            vaisalaApi.initialize()


            val notificationPermissionState =
                permissionsController.isGranted(PermissionNotifications)

            updateState {
                copy(
                    notificationPermissionState = notificationPermissionState,
                    // if true -> no need to show the screen
                    // if false -> either never shown it
                    notificationPermissionRequested = notificationPermissionState
                )
            }

            val apiProvider = sessionManager.initialize()

            Sentry.init {
                it.dsn = SharedConfig.sentrySecondary
            }

            GoogleAuthProvider.create(
                credentials = GoogleAuthCredentials(
                    serverId = SharedConfig.googleAuthServerId
                )
            )

            AuthentInit.initialize()

            val api = apiProvider?.apiNormal

            // and try to send the push notification token if known
            api?.let { makeAttemptSendPushNotification(it) }

            val sentinelAny = FirebaseWrapper.lastNotificationClicked?.get("sentinel")

            val thunderDetections: List<ThunderDetectionWithMetadata> =
                api?.detections() ?: emptyList()

            // extract the default route from the path OR notification
            val defaultRoute = if (null != sentinelAny && sentinelAny is String) {
                RouterDetection.navigateTo(sentinelAny).route
            } else {
                PossibleRoutes.defaultFromUrlLaunched(Navigation.originalPath())
            }

            val thunderDetection = if (defaultRoute is RouteParameterSentinel) {
                defaultRoute.uuid()
            } else {
                null
            }?.let { sentinel -> thunderDetections.firstOrNull { it.detection.uuid == sentinel } }

            updateState {
                copy(
                    initialized = true,
                    apiProvider = apiProvider,
                    currentApi = apiProvider?.apiNormal,
                    thunderDetections = thunderDetections /* + ThunderDetection.fakes */,
                    selectedThunderDetection = thunderDetection,
                    defaultRoute = defaultRoute
                )
            }

            safeLaunch {
                FirebaseWrapper.onToken.collect {
                    println("on new push notification token -> $it")
                    states.value.currentApi?.onPushNotificationToken(it)
                }
            }

            safeLaunch {
                FirebaseWrapper.onNotificationClicked.collect { payloadData ->
                    val sentinels = states.value.thunderDetections

                    println("FirebaseWrapper onNotificationClicked")

                    selectThunderDetection(sentinels.firstOrNull { it.detection.uuid == payloadData["sentinel"] })
                }
            }

            safeLaunch {
                FirebaseWrapper.onPushNotification.collect { notification ->
                    val sentinels = states.value.thunderDetections
                    val sentinel = notification.data?.get("sentinel")
                    println("FirebaseWrapper onPushNotification $sentinel")

                    sentinels.firstOrNull { it.detection.uuid == sentinel }?.let { sentinel ->
                        toaster?.show(
                            message = getString(Res.string.alert_detected_on_sentinel)
                                .replace("$1", sentinel.detection.name),
                            action = TextToastAction(
                                text = getString(Res.string.go),
                                onClick = {
                                    println("RouteDetection clicked onto $sentinel")
                                    selectThunderDetection(sentinel)
                                    toaster?.dismiss(it)
                                },
                            ),
                            duration = 20.seconds
                        )
                    }
                }
            }
        }
    }

    fun selectThunderDetection(thunderDetection: ThunderDetectionWithMetadata?) {
        safeLaunch {
            if (null == thunderDetection) {
                Navigation.setPath("/")
            }

            updateState {
                copy(
                    selectedThunderDetection = thunderDetection,
                    defaultRoute = if (null != thunderDetection) {
                        RouterDetection.navigateTo(thunderDetection).route
                    } else {
                        defaultRoute
                    },
                )
            }
        }
    }

    private fun makeAttemptSendPushNotification(api: ApiInterface) = safeAsync {
        try {
            api.onPushNotificationToken(FirebaseWrapper.getToken(viewModelScope))
            println("NOTIFICATION call sent")
        } catch (err: Throwable) {
            err.printStackTrace()
        }
    }

    fun requestPushNotificationPermissionRequested() {
        safeLaunch {
            updateState { copy(notificationPermissionRequested = true) }
        }
    }

    fun requestPushNotificationPermission() {
        safeLaunch {
            val api = states.value.currentApi
            updateState { copy(notificationPermissionRequested = null != api) }

            PermissionNotifications.let { notif ->
                val hasPermission = permissionsController.isGranted(notif)

                if (hasPermission) {
                    updateState { copy(notificationPermissionState = true) }

                    return@let
                }

                permissionsController.providePermission(notif)

                if (permissionsController.isGranted(notif)) {
                    api?.let { makeAttemptSendPushNotification(it) }
                }

                println(
                    "now has the notification permissions ? ${
                        permissionsController.isGranted(
                            notif
                        )
                    }"
                )
            }
        }
    }

    override fun register(email: String, password: String, callback: (err: Throwable?) -> Unit) {
        launch(
            onError = {
                callback(it)
            }
        ) {
            try {
                val apiProvider = sessionManager.register(email, password)

                val thunderDetections: List<ThunderDetectionWithMetadata> =
                    apiProvider.apiNormal.detections()

                val defaultRoute = PossibleRoutes
                    .defaultFromUrlLaunched(Navigation.originalPath())
                    .let { it as? RouteParameterSentinel }

                val thunderDetection = defaultRoute?.uuid()?.let { sentinel ->
                    thunderDetections.firstOrNull { it.detection.uuid == sentinel }
                }

                updateState {
                    copy(
                        apiProvider = apiProvider,
                        currentApi = apiProvider.apiNormal,
                        thunderDetections = thunderDetections, /* + ThunderDetection.fakes */
                        selectedThunderDetection = thunderDetection,
                    )
                }

                callback(null)
            } catch (err: Throwable) {
                callback(err)
            }
        }
    }

    override fun login(email: String, password: String, callback: (err: Throwable?) -> Unit) {
        launch(
            onError = {
                callback(it)
            }
        ) {
            try {
                val apiProvider = sessionManager.login(email, password)

                val thunderDetections: List<ThunderDetectionWithMetadata> =
                    apiProvider.apiNormal.detections()

                val defaultRoute = PossibleRoutes
                    .defaultFromUrlLaunched(Navigation.originalPath())
                    .let { it as? RouteParameterSentinel }

                val thunderDetection = defaultRoute?.uuid()?.let { sentinel ->
                    thunderDetections.firstOrNull { it.detection.uuid == sentinel }
                }

                updateState {
                    copy(
                        apiProvider = apiProvider,
                        currentApi = apiProvider.apiNormal,
                        thunderDetections = thunderDetections, /* + ThunderDetection.fakes */
                        selectedThunderDetection = thunderDetection,
                    )
                }

                callback(null)
            } catch (err: Throwable) {
                callback(err)
            }
        }
    }

    abstract fun createThunderDetectionSelectedModel(selectedThunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSelectedModel

    fun createNoThunderDetectionSelectedModel() = NoThunderDetectionSelectedModel(
        states.value.isSystemDarkMode,
        states.value.currentApi!!
    )

    fun logout() = safeLaunch {
        sessionManager.logout()

        updateState {
            copy(
                apiProvider = null,
                currentApi = null,
            )
        }
    }
}

class AppModelManager(isSystemDarkMode: Boolean) :
    AppModel(isSystemDarkMode) {
    override val warnCustomerText: StringResource
        get() = Res.string.warn_customer_text_manager

    override val appTitle: StringResource
        get() = Res.string.app_title_manager

    override fun warnCustomerAction() = Unit

    override fun createThunderDetectionSelectedModel(selectedThunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSelectedModel {
        return ThunderDetectionSelectedModelManager(
            states.value.isSystemDarkMode,
            selectedThunderDetection,
            states.value.currentApi!!
        )
    }
}

class AppModelCustomer(isSystemDarkMode: Boolean) :
    AppModel(isSystemDarkMode) {
    override val warnCustomerText: StringResource
        get() = Res.string.warn_customer_text_client

    override val appTitle: StringResource
        get() = Res.string.app_title_client

    override fun warnCustomerAction() = Unit

    override fun createThunderDetectionSelectedModel(selectedThunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSelectedModel {
        return ThunderDetectionSelectedModelCustomer(
            states.value.isSystemDarkMode,
            selectedThunderDetection,
            states.value.currentApi!!
        )
    }
}

@Serializable
data class SpecificData(
    val nothing: Boolean = false
)